import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import API from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import PaginationBar from "@/components/PaginationBar";
import { TableSkeleton } from "@/components/Skeletons";
import EmptyState from "@/components/EmptyState";
import ListToolbar from "@/components/ListToolbar";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Package,
  MoreVertical,
  ArrowUpCircle,
  ArrowDownCircle,
  History,
  Pencil,
  Trash2,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { formatDate } from "@/lib/format";

function formatTxDate(dateStr) {
  if (!dateStr) return { date: "—", time: "" };
  const d = new Date(dateStr);
  const date = d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  let hours = d.getHours();
  const minutes = d.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  const time = `${hours.toString().padStart(2, "0")}:${minutes} ${ampm}`;
  return { date, time };
}

export function InventoryPage() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [adjustModal, setAdjustModal] = useState({
    open: false,
    type: "increase",
    item: null,
    quantity: "",
    reason: "",
    error: "",
  });
  const [historyModal, setHistoryModal] = useState({ open: false, item: null });
  const [editModal, setEditModal] = useState({
    open: false,
    item: null,
    productName: "",
    companyName: "",
    batchNumber: "",
    inDate: "",
    customerId: "",
  });
  const [deleteModal, setDeleteModal] = useState({ open: false, item: null });

  const customersQ = useQuery({
    queryKey: ["customers-lite"],
    queryFn: () => API.get("/customers", { params: { limit: 100 } }).then((r) => r.data.data),
  });

  const productsQ = useQuery({
    queryKey: ["product-master-lite"],
    queryFn: () => API.get("/product-master", { params: { limit: 200 } }).then((r) => r.data.data || []),
  });

  const q = useQuery({
    queryKey: ["inventory", { page, search, customerId, startDate, endDate }],
    queryFn: () =>
      API.get("/inventory", {
        params: {
          page,
          limit: 20,
          search: search || undefined,
          customerId: customerId || undefined,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
        },
      }).then((r) => r.data),
  });

  const historyQ = useQuery({
    queryKey: ["inventory-history", historyModal.item?.id],
    queryFn: () => API.get("/inventory/" + historyModal.item?.id).then((r) => r.data.data || r.data),
    enabled: !!historyModal.open && !!historyModal.item?.id,
  });

  const adjustMutation = useMutation({
    mutationFn: ({ id, type, quantity, reason }) =>
      API.post("/inventory/" + id + "/" + type, { quantity: Number(quantity), reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["inventory"] });
      setAdjustModal({ open: false, type: "increase", item: null, quantity: "", reason: "", error: "" });
    },
    onError: (err) => alert(err.response?.data?.message || "Adjustment failed"),
  });

  const editMutation = useMutation({
    mutationFn: ({ id, data }) => API.patch("/inventory/" + id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["inventory"] });
      setEditModal({ open: false, item: null, productName: "", companyName: "", batchNumber: "", inDate: "", customerId: "" });
    },
    onError: (err) => alert(err.response?.data?.message || "Update failed"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => API.delete("/inventory/" + id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["inventory"] });
      setDeleteModal({ open: false, item: null });
    },
    onError: (err) => alert(err.response?.data?.message || "Delete failed"),
  });

  const items = q.data?.data || [];
  const meta = q.data?.meta?.pagination || { page: 1, totalPages: 1 };
  const productList = productsQ.data || [];

  return (
    <div data-testid="inventory-page" className="p-6">
      <PageHeader
        title="Inventory"
        subtitle="Stock batches physically stored in your warehouse. Add stock via a customer's row in Customers."
      />

      <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-3 mb-4 mt-6">
        <div className="flex-1">
          <ListToolbar
            search={search}
            onSearchChange={(v) => {
              setSearch(v);
              setPage(1);
            }}
            placeholder="Search product, company, batch…"
            testIdPrefix="inv"
          />
        </div>

        <div className="flex items-center gap-2">
          <Input
            type="date"
            className="w-36 h-10 text-xs"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              setPage(1);
            }}
          />
          <span className="text-xs text-zinc-400">to</span>
          <Input
            type="date"
            className="w-36 h-10 text-xs"
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value);
              setPage(1);
            }}
          />
          <Select
            value={customerId || "ALL"}
            onValueChange={(v) => {
              setCustomerId(v === "ALL" ? "" : v);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-48 h-10 text-xs">
              <SelectValue placeholder="All customers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All customers</SelectItem>
              {(customersQ.data || []).map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.businessName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-zinc-200 overflow-hidden">
        {q.isLoading ? (
          <TableSkeleton rows={6} />
        ) : items.length === 0 ? (
          <EmptyState
            icon={Package}
            title="No inventory yet"
            description="Add your first stock batch to get started."
          />
        ) : (
          <table className="w-full text-sm" data-testid="inventory-table">
            <thead className="sticky top-0 z-10 bg-zinc-50 shadow-sm">
              <tr className="bg-zinc-50 text-zinc-500 text-[11px] uppercase tracking-wider border-b border-zinc-200">
                <th className="text-left px-4 py-3">Stock ID</th>
                <th className="text-left px-4 py-3">Product</th>
                <th className="text-left px-4 py-3">Company</th>
                <th className="text-left px-4 py-3">Batch #</th>
                <th className="text-left px-4 py-3">Customer</th>
                <th className="text-left px-4 py-3">In date</th>
                <th className="text-right px-4 py-3">Total cartons</th>
                <th className="text-right px-4 py-3">Available</th>
                <th className="w-10 px-2 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {items.map((i) => {
                const cust = (customersQ.data || []).find((c) => c.id === i.customerId);
                return (
                  <tr key={i.id} className="hover:bg-zinc-50 transition" data-testid={"inv-row-" + i.id}>
                    <td className="px-4 py-3 font-mono text-xs font-semibold text-blue-600">
                      {i.businessId || i.id}
                    </td>
                    <td className="px-4 py-3 font-medium text-zinc-900">{i.productName}</td>
                    <td className="px-4 py-3 text-zinc-600">{i.companyName || "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs text-zinc-700">{i.batchNumber}</td>
                    <td className="px-4 py-3 text-zinc-600">{cust?.businessName || "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs text-zinc-600">
                      {formatDate(i.inDate)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-xs">{i.cartonQuantity}</td>
                    <td className="px-4 py-3 text-right font-mono text-xs font-semibold">{i.availableQuantity}</td>
                    <td className="px-2 py-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            className="p-1.5 rounded text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48 bg-white shadow-lg rounded-lg border border-zinc-100 p-1">
                          <DropdownMenuItem
                            onClick={() => setAdjustModal({ open: true, type: "increase", item: i, quantity: "", reason: "", error: "" })}
                            className="flex items-center gap-2 px-3 py-2 text-xs text-zinc-700 hover:bg-zinc-50 cursor-pointer rounded"
                          >
                            <ArrowUpCircle className="w-4 h-4 text-emerald-600" />
                            <span>Increase qty</span>
                          </DropdownMenuItem>

                          <DropdownMenuItem
                            onClick={() => setAdjustModal({ open: true, type: "decrease", item: i, quantity: "", reason: "", error: "" })}
                            className="flex items-center gap-2 px-3 py-2 text-xs text-zinc-700 hover:bg-zinc-50 cursor-pointer rounded"
                          >
                            <ArrowDownCircle className="w-4 h-4 text-amber-600" />
                            <span>Decrease qty</span>
                          </DropdownMenuItem>

                          <DropdownMenuItem
                            onClick={() => setHistoryModal({ open: true, item: i })}
                            className="flex items-center gap-2 px-3 py-2 text-xs text-zinc-700 hover:bg-zinc-50 cursor-pointer rounded"
                          >
                            <History className="w-4 h-4 text-zinc-500" />
                            <span>Transaction history</span>
                          </DropdownMenuItem>

                          <DropdownMenuSeparator className="my-1 border-t border-zinc-100" />

                          <DropdownMenuItem
                            onClick={() =>
                              setEditModal({
                                open: true,
                                item: i,
                                productName: i.productName || "",
                                companyName: i.companyName || "",
                                batchNumber: i.batchNumber || "",
                                inDate: i.inDate ? i.inDate.slice(0, 10) : "",
                                customerId: i.customerId || "",
                              })
                            }
                            className="flex items-center gap-2 px-3 py-2 text-xs text-zinc-700 hover:bg-zinc-50 cursor-pointer rounded"
                          >
                            <Pencil className="w-4 h-4 text-zinc-500" />
                            <span>Edit</span>
                          </DropdownMenuItem>

                          <DropdownMenuItem
                            onClick={() => setDeleteModal({ open: true, item: i })}
                            className="flex items-center gap-2 px-3 py-2 text-xs text-rose-600 hover:bg-rose-50 cursor-pointer rounded"
                          >
                            <Trash2 className="w-4 h-4 text-rose-600" />
                            <span>Delete</span>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {meta.totalPages > 1 && (
          <div className="p-4 border-t border-zinc-100">
            <PaginationBar page={page} totalPages={meta.totalPages} onPageChange={setPage} />
          </div>
        )}
      </div>

      {/* Adjust Quantity Modal */}
      <Dialog
        open={adjustModal.open}
        onOpenChange={(open) => !open && setAdjustModal({ ...adjustModal, open: false, error: "" })}
      >
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="text-base font-semibold">
              {adjustModal.type === "increase" ? "Increase Quantity" : "Decrease Quantity"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2 text-xs">
            <div className="flex items-center justify-between text-zinc-600 bg-zinc-50 p-2.5 rounded-lg border border-zinc-100">
              <div>
                <span className="font-semibold text-zinc-900 block">{adjustModal.item?.productName}</span>
                <span className="text-[11px] text-zinc-500">Batch: {adjustModal.item?.batchNumber}</span>
              </div>
              <div className="text-right">
                <span className="text-[10px] text-zinc-400 uppercase tracking-wider block">Available</span>
                <span className="font-mono font-bold text-zinc-900">{adjustModal.item?.availableQuantity ?? 0}</span>
              </div>
            </div>

            {adjustModal.error && (
              <div className="flex items-center gap-2 p-2.5 rounded-md bg-rose-50 border border-rose-200 text-rose-700 font-medium text-xs">
                <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
                <span>{adjustModal.error}</span>
              </div>
            )}

            <div>
              <label className="block text-zinc-600 mb-1 font-medium">Quantity (Cartons)</label>
              <Input
                type="number"
                min="1"
                className="h-9 text-xs"
                placeholder="Enter cartons"
                value={adjustModal.quantity}
                onChange={(e) => {
                  const val = e.target.value;
                  const num = Number(val);
                  let err = "";

                  if (val !== "" && num <= 0) {
                    err = "Quantity must be greater than 0.";
                  } else if (
                    adjustModal.type === "decrease" &&
                    adjustModal.item &&
                    num > (adjustModal.item.availableQuantity ?? 0)
                  ) {
                    err = `Cannot decrease more than available stock (${adjustModal.item.availableQuantity ?? 0} cartons).`;
                  }

                  setAdjustModal((prev) => ({
                    ...prev,
                    quantity: val,
                    error: err,
                  }));
                }}
              />
            </div>
            <div>
              <label className="block text-zinc-600 mb-1 font-medium">Reason (Optional)</label>
              <Input
                type="text"
                className="h-9 text-xs"
                placeholder="e.g. Audit correction, returned goods"
                value={adjustModal.reason}
                onChange={(e) => setAdjustModal({ ...adjustModal, reason: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <button
              type="button"
              className="px-3 py-1.5 text-xs text-zinc-600 hover:bg-zinc-100 rounded"
              onClick={() => setAdjustModal({ ...adjustModal, open: false, error: "" })}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={
                !adjustModal.quantity ||
                Number(adjustModal.quantity) <= 0 ||
                !!adjustModal.error ||
                adjustMutation.isPending
              }
              className={
                "px-3 py-1.5 text-xs text-white rounded font-medium flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed " +
                (adjustModal.type === "increase"
                  ? "bg-emerald-600 hover:bg-emerald-700"
                  : "bg-amber-600 hover:bg-amber-700")
              }
              onClick={() => {
                const qty = Number(adjustModal.quantity);
                if (qty <= 0) {
                  setAdjustModal((prev) => ({ ...prev, error: "Quantity must be greater than 0." }));
                  return;
                }
                if (
                  adjustModal.type === "decrease" &&
                  adjustModal.item &&
                  qty > (adjustModal.item.availableQuantity ?? 0)
                ) {
                  setAdjustModal((prev) => ({
                    ...prev,
                    error: `Cannot decrease more than available stock (${adjustModal.item.availableQuantity ?? 0} cartons).`,
                  }));
                  return;
                }

                adjustMutation.mutate({
                  id: adjustModal.item?.id,
                  type: adjustModal.type,
                  quantity: adjustModal.quantity,
                  reason: adjustModal.reason,
                });
              }}
            >
              {adjustMutation.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Confirm
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Production-Style Transaction History Modal */}
      <Dialog
        open={historyModal.open}
        onOpenChange={(open) => !open && setHistoryModal({ open: false, item: null })}
      >
        <DialogContent className="max-w-2xl bg-white p-6 rounded-2xl shadow-2xl border border-zinc-200">
          <DialogHeader className="flex flex-row items-start justify-between pr-8 pb-1 border-b-0">
            <div>
              <DialogTitle className="text-xl font-bold tracking-tight text-zinc-950">
                Transaction history
              </DialogTitle>
              <p className="text-sm text-zinc-500 mt-1">
                {historyModal.item?.productName} · Batch {historyModal.item?.batchNumber}
              </p>
            </div>
            {historyModal.item && (
              <div className="bg-zinc-50 border border-zinc-200/80 rounded-xl px-4 py-2 text-right mr-2">
                <div className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">Current Available</div>
                <div className="text-base font-bold font-mono text-zinc-900 leading-tight mt-0.5">
                  {historyQ.data?.availableQuantity ?? historyModal.item?.availableQuantity ?? 0}{" "}
                  <span className="text-xs font-normal text-zinc-500 font-sans">cartons</span>
                </div>
              </div>
            )}
          </DialogHeader>

          <div className="mt-4 max-h-[420px] overflow-y-auto rounded-xl border border-zinc-100">
            {historyQ.isLoading ? (
              <div className="py-12 flex justify-center items-center">
                <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
              </div>
            ) : (historyQ.data?.transactions || historyQ.data?.inventoryTransactions || []).length === 0 ? (
              <p className="text-zinc-400 py-8 text-center text-sm">No transaction records found.</p>
            ) : (
              <table className="w-full text-xs text-left">
                <thead>
                  <tr className="bg-zinc-50 text-[11px] font-semibold tracking-wider text-zinc-500 uppercase border-b border-zinc-100">
                    <th className="px-5 py-3 w-44">WHEN</th>
                    <th className="px-4 py-3 w-28">TYPE</th>
                    <th className="px-4 py-3 text-right w-20">QTY</th>
                    <th className="px-5 py-3">REASON</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100 font-mono">
                  {(historyQ.data?.transactions || historyQ.data?.inventoryTransactions || []).map((t) => {
                    const rawType = (t.type || "").toUpperCase();
                    let typeClass = "text-blue-600 font-semibold";
                    if (rawType.includes("INCREASE")) typeClass = "text-emerald-600 font-bold";
                    if (rawType.includes("DECREASE")) typeClass = "text-amber-700 font-bold";
                    if (rawType.includes("DISPATCH")) typeClass = "text-red-600 font-bold";

                    const { date, time } = formatTxDate(t.createdAt);

                    return (
                      <tr key={t.id} className="hover:bg-zinc-50/70 transition">
                        <td className="px-5 py-3.5 text-zinc-800 leading-relaxed font-sans text-xs whitespace-nowrap">
                          {date},<br />
                          <span className="text-zinc-500 text-[11px] font-mono">{time}</span>
                        </td>
                        <td className={"px-4 py-3.5 text-[11px] uppercase tracking-wider " + typeClass}>
                          {rawType}
                        </td>
                        <td className="px-4 py-3.5 text-right text-zinc-900 font-medium text-xs">
                          {Math.abs(t.quantity || t.changeQuantity || t.cartons || 0)}
                        </td>
                        <td className="px-5 py-3.5 text-zinc-600 font-sans text-xs leading-normal">
                          {t.dispatchRequest?.businessId ? (
                            <span>Dispatch approved (<span className="font-mono font-semibold text-zinc-900">{t.dispatchRequest.businessId}</span>)</span>
                          ) : t.dispatchBusinessId ? (
                            <span>Dispatch approved (<span className="font-mono font-semibold text-zinc-900">{t.dispatchBusinessId}</span>)</span>
                          ) : (
                            t.reason || t.notes || "—"
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Inventory Modal with Product Dropdown & Readonly Company */}
      <Dialog
        open={editModal.open}
        onOpenChange={(open) => !open && setEditModal({ ...editModal, open: false })}
      >
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="text-base font-semibold">Edit Stock Batch</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2 text-xs">
            <div>
              <label className="block text-zinc-600 mb-1 font-medium">Product Name</label>
              <Select
                value={editModal.productName}
                onValueChange={(val) => {
                  const prod = productList.find((p) => (p.name || p.productName) === val);
                  setEditModal((prev) => ({
                    ...prev,
                    productName: val,
                    companyName: prod?.companyName || prod?.company || "",
                  }));
                }}
              >
                <SelectTrigger className="w-full h-9 text-xs">
                  <SelectValue placeholder="Select product" />
                </SelectTrigger>
                <SelectContent className="max-h-60 bg-white">
                  {productList.map((p) => {
                    const name = p.name || p.productName;
                    return (
                      <SelectItem key={p.id || name} value={name}>
                        {name} {p.companyName ? "(" + p.companyName + ")" : ""}
                      </SelectItem>
                    );
                  })}
                  {editModal.productName && !productList.some((p) => (p.name || p.productName) === editModal.productName) && (
                    <SelectItem value={editModal.productName}>
                      {editModal.productName}
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-zinc-600 mb-1 font-medium">Company Name</label>
              <Input
                type="text"
                className="h-9 text-xs bg-zinc-100 text-zinc-500 cursor-not-allowed border-zinc-200"
                value={editModal.companyName || "—"}
                disabled
                readOnly
              />
            </div>
            <div>
              <label className="block text-zinc-600 mb-1 font-medium">Batch #</label>
              <Input
                type="text"
                className="h-9 text-xs"
                value={editModal.batchNumber}
                onChange={(e) => setEditModal({ ...editModal, batchNumber: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-zinc-600 mb-1 font-medium">In Date</label>
              <Input
                type="date"
                className="h-9 text-xs"
                value={editModal.inDate}
                onChange={(e) => setEditModal({ ...editModal, inDate: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <button
              type="button"
              className="px-3 py-1.5 text-xs text-zinc-600 hover:bg-zinc-100 rounded"
              onClick={() => setEditModal({ ...editModal, open: false })}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={editMutation.isPending || !editModal.productName}
              className="px-3 py-1.5 text-xs text-white bg-black hover:bg-zinc-800 rounded font-medium flex items-center gap-1.5"
              onClick={() =>
                editMutation.mutate({
                  id: editModal.item?.id,
                  data: {
                    productName: editModal.productName,
                    companyName: editModal.companyName,
                    batchNumber: editModal.batchNumber,
                    inDate: editModal.inDate ? new Date(editModal.inDate).toISOString() : undefined,
                  },
                })
              }
            >
              {editMutation.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Save Changes
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Alert Dialog */}
      <AlertDialog
        open={deleteModal.open}
        onOpenChange={(open) => !open && setDeleteModal({ open: false, item: null })}
      >
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-base font-semibold">Delete Stock Item?</AlertDialogTitle>
            <AlertDialogDescription className="text-xs text-zinc-600">
              Are you sure you want to delete <span className="font-semibold text-zinc-900">{deleteModal.item?.productName}</span> (Batch: {deleteModal.item?.batchNumber})? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="text-xs">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-rose-600 hover:bg-rose-700 text-white text-xs"
              onClick={() => deleteMutation.mutate(deleteModal.item?.id)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default InventoryPage;
